import { useNavigate } from "react-router-dom";
import { Briefcase, Coins, TrendingUp, Calendar, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCareerActivation } from "@/hooks/useCareerActivation";
import { useDailyPaycheck } from "@/hooks/useDailyPaycheck";
import HoloPlacard from "@/components/holo/HoloPlacard";

/**
 * Financial Command Center — Career & Paycheck panel.
 * Shows the activated career, side hustle metrics, and recent paycheck history.
 */
export default function CareerPanel() {
  const navigate = useNavigate();
  const { state, activated, loading } = useCareerActivation();
  const { history } = useDailyPaycheck();

  if (loading) return null;

  if (!activated || !state) {
    return (
      <HoloPlacard className="p-4">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-muted/40 flex items-center justify-center shrink-0">
            <Briefcase className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-bold text-foreground">No career activated yet</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Complete the Career Path module to start receiving daily paychecks and unlock career events.
            </p>
            <Button size="sm" variant="outline" className="mt-3 gap-1.5"
              onClick={() => navigate("/career-path")}>
              Start Career Path <ArrowRight className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </HoloPlacard>
    );
  }

  const compLabel =
    state.comp_type === "hourly" || state.comp_type === "contract"
      ? `$${state.hourly_rate ?? 0}/hr · ~${state.weekly_hours ?? 0} hrs/wk`
      : state.comp_type === "base_plus_commission"
      ? `Base $${state.annual_income.toLocaleString()}/yr + commission`
      : `$${state.annual_income.toLocaleString()}/yr salary`;

  const sh = state.side_hustle_metrics || {};
  const careerTitle = state.career_key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  const recent = history.slice(0, 7);

  return (
    <div className="space-y-3">
      <HoloPlacard variant="glow" className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-primary shrink-0" />
              <p className="text-[10px] tracking-[0.2em] uppercase text-primary/80 font-semibold">Career</p>
            </div>
            <h3 className="font-heading text-lg font-bold text-foreground mt-1">{careerTitle}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{compLabel}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Activated</p>
            <p className="text-xs text-foreground mt-1">
              {new Date(state.activated_at).toLocaleDateString()}
            </p>
          </div>
        </div>

        {state.side_hustle_key && state.side_hustle_key !== "none" && (
          <div className="mt-3 rounded-xl border border-border/60 bg-card/40 p-3">
            <div className="flex items-center gap-2">
              <Coins className="h-3.5 w-3.5 text-accent" />
              <p className="text-xs font-semibold text-foreground">
                Side hustle: {(state.side_hustle_key as string).replace(/_/g, " ")}
              </p>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
              {Object.entries(sh)
                .filter(([k]) => k !== "type")
                .map(([k, v]) => (
                  <div key={k} className="rounded-md bg-muted/30 px-2 py-1.5">
                    <p className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}</p>
                    <p className="font-mono text-foreground">{String(v)}</p>
                  </div>
                ))}
            </div>
          </div>
        )}
      </HoloPlacard>

      <HoloPlacard className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-primary" />
            <h4 className="font-heading text-sm font-bold text-foreground">Recent paychecks</h4>
          </div>
          <span className="text-[10px] text-muted-foreground">Last 7 days</span>
        </div>
        {recent.length === 0 ? (
          <p className="text-xs text-muted-foreground py-3 text-center">
            Your first paycheck arrives at your next daily check-in.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="py-1.5 pr-2 font-medium">Date</th>
                  <th className="py-1.5 px-2 font-medium text-right">Gross</th>
                  <th className="py-1.5 px-2 font-medium text-right">Fed</th>
                  <th className="py-1.5 px-2 font-medium text-right">State</th>
                  <th className="py-1.5 px-2 font-medium text-right">Payroll</th>
                  <th className="py-1.5 px-2 font-medium text-right">Benefits</th>
                  <th className="py-1.5 pl-2 font-medium text-right">Net</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {recent.map(p => (
                  <tr key={p.id} className="text-foreground">
                    <td className="py-1.5 pr-2 whitespace-nowrap">{p.pay_date}</td>
                    <td className="py-1.5 px-2 font-mono text-right">${Number(p.gross_pay).toFixed(2)}</td>
                    <td className="py-1.5 px-2 font-mono text-right text-destructive/80">
                      −${Number(p.federal_tax).toFixed(2)}
                    </td>
                    <td className="py-1.5 px-2 font-mono text-right text-destructive/80">
                      −${Number(p.state_tax).toFixed(2)}
                    </td>
                    <td className="py-1.5 px-2 font-mono text-right text-destructive/80">
                      −${(Number(p.social_security) + Number(p.medicare)).toFixed(2)}
                    </td>
                    <td className="py-1.5 px-2 font-mono text-right text-destructive/80">
                      −${Number(p.benefits).toFixed(2)}
                    </td>
                    <td className="py-1.5 pl-2 font-mono text-right text-primary font-semibold">
                      ${Number(p.net_pay).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-2 text-center text-[10px] text-muted-foreground">
          <TrendingUp className="inline h-3 w-3 mr-1 opacity-70" />
          Educational estimates. Career events affect future checks after activation.
        </p>
      </HoloPlacard>
    </div>
  );
}
